import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./LatestArticles.scss";
import ArticleBanner from "../../assets/publicworks.jpg";
import { encodeArticleSlug } from "../articleDetail/articleSlug";
import { getLatestArticles } from "../../actions/articles.action";
import { PATHS } from "../../constant";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const DEFAULT_TAGS = [];
const LatestArticlesLeftArrow = () => (
  <svg
    className="latest-articles-arrow-icon latest-articles-arrow-icon-left"
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="60"
    viewBox="0 0 30 60"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.60766 28.2224L18.7502 14.0799L22.2852 17.6149L9.91016 29.9899L22.2852 42.3649L18.7502 45.8999L4.60766 31.7574C4.13898 31.2886 3.87569 30.6528 3.87569 29.9899C3.87569 29.327 4.13898 28.6912 4.60766 28.2224Z"
      fill="#B3B3B3"
    />
  </svg>
);

const LatestArticlesRightArrow = () => (
  <svg
    className="latest-articles-arrow-icon latest-articles-arrow-icon-right"
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="32"
    viewBox="0 0 19 32"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.6775 17.6775L3.535 31.82L0 28.285L12.375 15.91L0 3.535L3.535 0L17.6775 14.1425C18.1462 14.6113 18.4095 15.2471 18.4095 15.91C18.4095 16.5729 18.1462 17.2087 17.6775 17.6775Z"
      fill="#B3B3B3"
    />
  </svg>
);

const getImageUrl = article => {
  const image =
    article &&
    Array.isArray(article.images) &&
    article.images.find(item => item && item.s3Url);

  return (image && image.s3Url) || ArticleBanner;
};

const getArticleTags = article => {
  const tags = article && (article.tags || article.tag || article.tagIds);

  if (!Array.isArray(tags) || !tags.length) {
    return DEFAULT_TAGS;
  }

  return tags
    .map(tag =>
      typeof tag === "string" || typeof tag === "number"
        ? String(tag)
        : tag && (tag.name || tag.label || tag.title || tag.value)
    )
    .filter(Boolean);
};

const LatestArticles = ({ limit = 4, active = true }) => {
  const [articles, setArticles] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [titleTooltipWidth, setTitleTooltipWidth] = useState(null);
  const fetchArticles = async nextOffset => {
    setLoading(true);
    try {
      const response = await getLatestArticles({
        offset: nextOffset,
        limit,
        active
      });

      setArticles(
        Array.isArray(response && response.data) ? response.data : []
      );
      setTotalCount(
        (response && (response.total_count || response.total)) || 0
      );
      setTotalPages(
        Number(response && (response.total_pages || response.totalPages)) || 0
      );
      setOffset(nextOffset);
    } catch (error) {
      console.error("Error fetching latest articles:", error);
      setArticles([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, active]);

  const hasPrevious = offset > 0;
  const hasArticles = articles.length > 0;
  const hasNext = hasArticles
    ? totalPages
      ? Number(offset) + 1 < totalPages
      : Number(offset) + limit < totalCount
    : false;

  const handlePrevious = () => {
    if (!hasPrevious || loading) return;

    fetchArticles(Number(offset) - 1);
  };

  const handleNext = () => {
    if (!hasArticles || !hasNext || loading) return;

    fetchArticles(Number(offset) + 1);
  };

  const formatArticleDate = value => {
    if (!value) return "";

    const rawDate = String(value).trim();
    const ddmmyyyyMatch = rawDate.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    const parsedDate = ddmmyyyyMatch
      ? new Date(
          Number(ddmmyyyyMatch[3]),
          Number(ddmmyyyyMatch[2]) - 1,
          Number(ddmmyyyyMatch[1])
        )
      : new Date(rawDate);

    if (Number.isNaN(parsedDate.getTime())) {
      return rawDate;
    }

    return parsedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };
  return (
    articles.length > 0 && (
      <section className="latest-articles-section">
        <h2>Latest Articles</h2>

        <button
          type="button"
          className="latest-articles-arrow latest-articles-arrow-left"
          onClick={handlePrevious}
          disabled={!hasPrevious || loading}
          aria-label="Previous latest articles"
        >
          <LatestArticlesLeftArrow />
        </button>

        <div className={`latest-articles-grid ${loading ? "is-loading" : ""}`}>
          {articles.map(article => (
            <Link
              to={`${PATHS.ARTICLES_DETAILS_PAGE}/${encodeArticleSlug(
                article.id,
                article.title
              )}`}
              className="latest-article-card"
              key={article.id}
            >
              <img
                src={getImageUrl(article)}
                alt={article.title || "Article"}
                onError={event => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = ArticleBanner;
                }}
              />
              <div className="latest-article-content">
                <span className="latest-article-date">
                  {formatArticleDate(article.createdAt)}
                </span>
                <h3>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip
                        id={`latest-article-title-tooltip-${article.id}`}
                        className="mis-tooltip latest-article-title-tooltip"
                        placement="top"
                        style={
                          titleTooltipWidth
                            ? {
                                "--latest-article-title-width": `${titleTooltipWidth}px`
                              }
                            : null
                        }
                      >
                        {article.title}
                      </Tooltip>
                    }
                  >
                    <span
                      className="latest-article-title"
                      onFocus={event =>
                        setTitleTooltipWidth(
                          Math.ceil(
                            event.currentTarget.getBoundingClientRect().width
                          )
                        )
                      }
                      onMouseEnter={event =>
                        setTitleTooltipWidth(
                          Math.ceil(
                            event.currentTarget.getBoundingClientRect().width
                          )
                        )
                      }
                    >
                      {article.title}
                    </span>
                  </OverlayTrigger>
                </h3>
                <div className="latest-article-summary">
                  {article.shortSummary && (
                    <p className="latest-article-summary-text">
                      {article.shortSummary}
                    </p>
                  )}
                </div>
                <div className="latest-article-tags">
                  {getArticleTags(article).map(tag => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {!loading && !articles.length && (
          <div className="latest-articles-empty">No articles found</div>
        )}

        <button
          type="button"
          className="latest-articles-arrow latest-articles-arrow-right"
          onClick={handleNext}
          disabled={!hasArticles || !hasNext || loading}
          aria-label="Next latest articles"
        >
          <LatestArticlesRightArrow />
        </button>
      </section>
    )
  );
};

export default LatestArticles;
